import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LoginCredentials } from '../../types/auth';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/custom/Dialog';
import { useTheme } from '../../contexts/ThemeContext';
import { getSubdomain } from '../../utils/tenantUtils';
import ReCAPTCHA from 'react-google-recaptcha';

interface ForgotPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onForgotPassword: (email: string, subdomain: string) => Promise<void>;
}

function ForgotPasswordDialog({ open, onOpenChange, onForgotPassword }: ForgotPasswordDialogProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<{ email: string; subdomain: string }>();

  const handleFormSubmit = async (data: { email: string; subdomain: string }) => {
    try {
      await onForgotPassword(data.email, data.subdomain);
      reset();
      onOpenChange(false);
    } catch {
      // Error handling is done in the parent component
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={theme === 'dark' ? 'bg-gray-800 border-gray-700' : undefined}>
        <DialogHeader>
          <DialogTitle className={theme === 'dark' ? 'text-gray-100' : undefined}>
            {t('auth.forgotPasswordTitle')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="py-4 space-y-4">
          <div>
            <label
              htmlFor="forgot-subdomain"
              className={`block text-sm font-medium ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              {t('auth.subdomain', 'Subdomain')}
            </label>
            <div className="mt-1">
              <input
                {...register('subdomain', {
                  required: t('auth.subdomainRequired', 'Subdomain is required'),
                  pattern: {
                    value: /^[a-z0-9-]+$/i,
                    message: t('auth.invalidSubdomain', 'Invalid subdomain format')
                  }
                })}
                type="text"
                id="forgot-subdomain"
                autoComplete="organization"
                className={`appearance-none block w-full px-3 py-2 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 sm:text-sm ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-400'
                    : 'border-gray-300 focus:border-blue-500'
                }`}
                placeholder={t('auth.subdomainPlaceholder', 'your-company')}
              />
              {errors.subdomain && (
                <p
                  className={`mt-1 text-sm animate-[fadeIn_0.2s_ease-in] ${
                    theme === 'dark' ? 'text-red-400' : 'text-red-600'
                  }`}
                >
                  {errors.subdomain.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="forgot-email"
              className={`block text-sm font-medium ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              {t('auth.emailAddress')}
            </label>
            <div className="mt-1">
              <input
                {...register('email', {
                  required: t('auth.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('auth.invalidEmail')
                  }
                })}
                type="email"
                id="forgot-email"
                autoComplete="email"
                className={`appearance-none block w-full px-3 py-2 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 sm:text-sm ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-400'
                    : 'border-gray-300 focus:border-blue-500'
                }`}
                placeholder={t('auth.emailPlaceholder')}
              />
              {errors.email && (
                <p
                  className={`mt-1 text-sm animate-[fadeIn_0.2s_ease-in] ${
                    theme === 'dark' ? 'text-red-400' : 'text-red-600'
                  }`}
                >
                  {errors.email.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                theme === 'dark'
                  ? 'text-gray-300 bg-gray-600 hover:bg-gray-500 focus:ring-gray-500 focus:ring-offset-gray-800'
                  : 'text-gray-700 bg-gray-200 hover:bg-gray-300 focus:ring-gray-500'
              } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                theme === 'dark'
                  ? 'bg-blue-600 hover:bg-blue-500 focus:ring-blue-400 focus:ring-offset-gray-800'
                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
              } ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2 inline-block"></div>
                  {t('common.sending', 'Sending...')}
                </>
              ) : (
                t('auth.sendResetLink', 'Send Reset Link')
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface LoginFormProps {
  onSubmit: (credentials: LoginCredentials & { subdomain?: string }) => Promise<void>;
  onCaptcha: (token: string) => Promise<void>;
  isCaptchaVerified: boolean;
  onForgotPassword: (email: string, subdomain: string) => Promise<void>;
}

export default function LoginForm({
  onSubmit,
  onCaptcha,
  isCaptchaVerified,
  onForgotPassword
}: LoginFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<LoginCredentials & { subdomain: string }>();
  const currentSubdomain = getSubdomain();
  const [error, setError] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [showAdminDialog, setShowAdminDialog] = React.useState(false);
  const { theme } = useTheme();

  const canLogin = useMemo(() => {
    const isDevelopment = import.meta.env.MODE === 'development';
    return !error && !isSubmitting && (isDevelopment || isCaptchaVerified);
  }, [error, isSubmitting, isCaptchaVerified]);

  const handleLogin = async (data: LoginCredentials) => {
    try {
      setError('');
      await onSubmit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.loginFailed'));
    }
  };

  const handleCaptchaChange = (token: string | null) => {
    if (token) {
      onCaptcha(token);
    }
  };

  return (
    <>
      <form className="mt-8 space-y-6" onSubmit={handleSubmit(handleLogin)}>
        {error && (
          <div
            className={`rounded-md p-4 animate-[fadeIn_0.2s_ease-in] ${
              theme === 'dark' ? 'bg-red-900/30' : 'bg-red-50'
            }`}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className={`h-5 w-5 ${theme === 'dark' ? 'text-red-400' : 'text-red-400'}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3
                  className={`text-sm font-medium ${
                    theme === 'dark' ? 'text-red-200' : 'text-red-800'
                  }`}
                >
                  {error}
                </h3>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {!currentSubdomain && (
            <div>
              <label
                htmlFor="subdomain"
                className={`block text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                {t('auth.organizationSubdomain')}
              </label>
              <div className="mt-1">
                <div className="flex rounded-md shadow-sm">
                  <input
                    {...register('subdomain', {
                      required: t('auth.subdomainRequired'),
                      pattern: {
                        value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/i,
                        message: t('auth.invalidSubdomain')
                      }
                    })}
                    type="text"
                    className={`appearance-none block w-full px-3 py-2 rounded-l-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 sm:text-sm ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-400'
                        : 'border-gray-300 focus:border-blue-500'
                    }`}
                    placeholder={t('auth.subdomainPlaceholder')}
                  />
                  <span
                    className={`inline-flex items-center px-3 rounded-r-md border border-l-0 sm:text-sm ${theme === 'dark' ? 'bg-gray-600 border-gray-500 text-gray-300' : 'bg-gray-50 border-gray-300 text-gray-500'}`}
                  >
                    .flowcxp.com
                  </span>
                </div>
                {errors.subdomain && (
                  <p
                    className={`mt-1 text-sm animate-[fadeIn_0.2s_ease-in] ${
                      theme === 'dark' ? 'text-red-400' : 'text-red-600'
                    }`}
                  >
                    {errors.subdomain.message}
                  </p>
                )}
                <div
                  className={`mt-2 p-3 rounded-md ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-blue-50'}`}
                >
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className={`h-5 w-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-400'}`}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p
                        className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                      >
                        {t('auth.subdomainHelpText')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {currentSubdomain && (
            <div
              className={`mb-4 p-3 rounded-md ${theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-50'}`}
            >
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className={`h-5 w-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-400'}`}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('auth.loggingIntoOrg')}{' '}
                    <strong className={theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}>
                      {currentSubdomain}
                    </strong>{' '}
                    organization.
                    {window.location.hostname === 'localhost' && <span> {t('auth.devMode')}</span>}
                  </p>
                </div>
              </div>
            </div>
          )}
          <div>
            <label
              htmlFor="email"
              className={`block text-sm font-medium ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              {t('auth.emailAddress')}
            </label>
            <div className="mt-1">
              <input
                {...register('email', {
                  required: t('auth.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('auth.invalidEmail')
                  }
                })}
                type="email"
                autoComplete="email"
                className={`appearance-none block w-full px-3 py-2 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 sm:text-sm ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-400'
                    : 'border-gray-300 focus:border-blue-500'
                }`}
                placeholder={t('auth.emailPlaceholder')}
              />
              {errors.email && (
                <p
                  className={`mt-1 text-sm animate-[fadeIn_0.2s_ease-in] ${
                    theme === 'dark' ? 'text-red-400' : 'text-red-600'
                  }`}
                >
                  {errors.email.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="password"
              className={`block text-sm font-medium ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              {t('auth.password')}
            </label>
            <div className="mt-1 relative">
              <input
                {...register('password', {
                  required: t('auth.passwordRequired'),
                  minLength: {
                    value: 6,
                    message: t('auth.passwordMinLength')
                  }
                })}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className={`appearance-none block w-full px-3 py-2 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 sm:text-sm pr-10 ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-400'
                    : 'border-gray-300 focus:border-blue-500'
                }`}
                placeholder={t('auth.passwordPlaceholder')}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff
                    className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`}
                  />
                ) : (
                  <Eye
                    className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`}
                  />
                )}
              </button>
              {errors.password && (
                <p
                  className={`mt-1 text-sm animate-[fadeIn_0.2s_ease-in] ${
                    theme === 'dark' ? 'text-red-400' : 'text-red-600'
                  }`}
                >
                  {errors.password.message}
                </p>
              )}
            </div>
          </div>

          <div className="text-sm">
            <button
              type="button"
              onClick={() => setShowAdminDialog(true)}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              {t('auth.forgotPassword')}
            </button>
          </div>
        </div>

        {/* Captcha */}
        {import.meta.env.MODE !== 'development' && (
          <div className="flex justify-center items-center">
            <ReCAPTCHA
              sitekey={import.meta.env.VITE_CAPTCHA_SITE_KEY}
              onChange={handleCaptchaChange}
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Cancel Button */}
          <button
            type="button"
            onClick={() => navigate('/')}
            disabled={isSubmitting}
            className={`flex-1 flex justify-center py-2 px-4 border rounded-md shadow-sm text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              theme === 'dark'
                ? 'border-gray-600 text-gray-300 bg-gray-700/50 hover:bg-gray-700 focus:ring-gray-500 focus:ring-offset-gray-800'
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-gray-500'
            } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            Back to Home
          </button>

          {/* Sign In Button */}
          <button
            type="submit"
            disabled={!canLogin}
            className={`flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              theme === 'dark'
                ? 'bg-blue-600 hover:bg-blue-500 focus:ring-blue-400 focus:ring-offset-gray-800'
                : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
            } ${!canLogin ? 'opacity-75 cursor-not-allowed' : 'transform hover:scale-[1.02]'}`}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                {t('auth.signingIn')}
              </>
            ) : (
              <>
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                  />
                </svg>
                {t('auth.signIn')}
              </>
            )}
          </button>
        </div>
      </form>
      <ForgotPasswordDialog
        open={showAdminDialog}
        onOpenChange={setShowAdminDialog}
        onForgotPassword={onForgotPassword}
      />
    </>
  );
}
